import { CardSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import {
  updateOutboxGesecInvoiceCache,
  useBusinessCentralOutboxGesecInvoicePutMutation,
  useBusinessCentralOutboxGesecInvoicesShowQuery,
} from "@/services/backend/business-central/api/outbox-gesec-invoice-api";
import { OutboxBaseCard } from "./outbox-base-card";
import { OutboxStatus } from "../types/outbox";
import { OutboxGesecInvoice } from "../types/outbox-gesec-invoice";
import { OutboxDocumentReferencesCard } from "./outbox-document-references-card";
import { GesecInvoiceFields } from "./gesec-invoice-fields";

export function OutboxGesecInvoicePage({ outboxId }: { outboxId: string }) {
  const { data, isLoading, error } =
    useBusinessCentralOutboxGesecInvoicesShowQuery({
      outboxId,
    });

  if (isLoading) {
    return <CardSkeleton />;
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  // we make sure that the child will get a value from the RTK Query cache
  if (!data) {
    return null;
  }

  return <OutboxGesecInvoiceUpdater data={data} />;
}

function OutboxGesecInvoiceUpdater({ data }: { data: OutboxGesecInvoice }) {
  const [set] = useOptimisticDebouncedMutation(
    data,
    updateOutboxGesecInvoiceCache,
    useBusinessCentralOutboxGesecInvoicePutMutation(),
  );
  const disabled = data.outboxStatus === OutboxStatus.Sent;

  return (
    <div className="grid gap-2">
      <div className="grid gap-2 md:grid-cols-2">
        <OutboxBaseCard
          outboxBase={data}
          onOutboxBaseChange={(base) => set({ ...data, ...base })}
          disabled={disabled}
        />
        <OutboxDocumentReferencesCard
          outbox={data}
          onOutboxChange={(documents) => set({ ...data, ...documents })}
          disabled={disabled}
        />
      </div>
      <GesecInvoiceFields
        invoice={data}
        onInvoiceChange={(invoice) => set({ ...data, ...invoice })}
        disabled={disabled}
      />
    </div>
  );
}
